/*
Author: LiuBo
Date: 2018-09-05
Desc: 请求对象，封装axios，由main.js 引入，页面通过 this.$req 调用
*/
import Vue from "vue";
import axios from "axios";
import conf from "@/config/index";
import cache from "@/assets/js/cache";
import sign from "@/assets/js/sign";
import router from "@/router";
import store from "@/store/index";
import log from "@/assets/js/log";
import { openRequestLoading } from "@/assets/js/request_loading";

let reqLoader = {};
let count = 0;
let timeoutAlertFlag = false; // 超时弹框标志。当页面由多个异步请求超时时，会弹出多个提示框影响体验。此标志用于判断是否已经有弹框，从而确保只有一次弹框。
// 检查重复请求,主要逻辑：
// 1，进入request拦截器后，调用removePending() 方法，检查在pending队列中是否存在跟当前请求相同的请求，如果有，调用前一个请求的f()方法进行取消
// 2, 将当前请求加入队列
// 3, 请求成功后，response拦截器同样调用removePending() 方法，将当前请求从pending中移除
let pending = [];
let CancelToken = axios.CancelToken;
let removePending = (config) => {
    for (let p in pending) {
        if ((config.url + "&" + config.method).indexOf(pending[p].u) > -1 && !config.repeat) {
            if (reqLoader[pending[p]._loader]) {
                reqLoader[pending[p]._loader].close();
            }
            pending[p].f();
            pending.splice(p, 1);
        }
    }
};
const clearPending = () => {
    for (let p in pending) {
        if (reqLoader[pending[p]._loader]) {
            reqLoader[pending[p]._loader].close();
        }
        pending[p].f();
        pending.splice(p, 1);
        clearPending();
        break;
    }
};
// 记录重复请求 ends
axios.defaults.timeout = 5000;
axios.defaults.baseURL = conf.api.baseUrl;
// 添加请求拦截器
axios.interceptors.request.use(
    function (config) {
        // 在发送请求之前
        // 组织公参
        const _x = sign.uuid();
        let _sign = "";
        if (config.method === "get" || config.method === "delete") {
            config.params = Object.assign({}, config.params, { _msgid: _x });
            _sign = sign.getSign(config.params);
        } else {
            if (!(config.headers && config.headers["Content-Type"] === "multipart/form-data")) {
                config.data = Object.assign({}, config.data, { _msgid: _x });
                _sign = sign.getSign(config.data);
            }
        }
        if (conf.session_mode === "1") {
            config.headers._msgid = _x;
            config.headers._sign = _sign;
        }
        // 异步调用原生方法获取每个请求的sign值
        // 组织公参 ends
        // 检查重复请求
        removePending(config);
        // 将当前请求加入pending队列
        config.cancelToken = new CancelToken((c) => {
            pending.push({
                u: config.url + "&" + config.method,
                f: c,
                _loader: config._loader
            });
        });
        log.trace("[" + config.url + "] 的详细请求参数: ", config);
        return config;
    },
    function (error) {
        // 请求错误
        log.fatal("发送请求错误: ", error);
        // reqLoader.close()
        return Promise.reject(error);
    }
);

// 添加响应拦截器
axios.interceptors.response.use(
    function (response) {
        // 服务端响应后
        // 从pending队列中移除当前请求
        removePending(response.config);
        if (response.config._loader) {
            reqLoader[response.config._loader].close();
        }
        let data = response.data;
        // 针对文件下载返回文件流
        if (response.request.responseType === "arraybuffer") {
            let datas = {
                alllength: "1",
                code: "0000000",
                msg: "下载内容",
                record: [response.data]
            };
            return datas;
        }
        let code = data.code;
        log.info("服务端返回: ", data);
        let d3 = code.charAt(2);
        if (code === "0000000") {
            // 接口返回成功，此时将数据传递到页面
            return data;
        } else if (code === "9999999") {
            // 接口超时，此时应清空所有还未发出的请求，以免重复报超时
            clearPending();
            // 接口返回超时，此时有两种情况：
            // 之前已登录，此时应通知框架更新登录状态为未登录，跳到login页面，并提示用户
            if (!timeoutAlertFlag) {
                timeoutAlertFlag = true;
                const _sessionData =
                    response.config.url.indexOf("common/session") > -1 ? data.record[0][0] : "";
                store.dispatch("logout", _sessionData).then((res) => {
                    Vue.prototype
                        .$alert(
                            "您尚未登录，或已长时间未进行操作，为了保障您的账户安全，请刷新后重新登录!",
                            "",
                            {
                                confirmButtonText: "确定",
                                type: "warning",
                                callback: (action) => {
                                    timeoutAlertFlag = false;
                                    router.push("/");
                                }
                            }
                        )
                        .then(() => {
                            return false;
                        })
                        .catch(() => {
                            return false;
                        });
                });
            }
            return Promise.reject(data);
        } else if (code === "9999998") {
            // 账号在别处登录。这种情况应该只出现在DAC应用中，DB记录的用户状态被删除导致，其他未使用JDAC的应用如终端系统不应该出现此情况
            clearPending();
            if (!timeoutAlertFlag) {
                timeoutAlertFlag = true;
                Vue.prototype
                    .$alert("您的账号可能已经在别处登录，请重新登录!", "", {
                        confirmButtonText: "确定",
                        type: "warning",
                        callback: (action) => {
                            timeoutAlertFlag = false;
                            store.dispatch("logout").then((res) => {
                                router.push("/");
                            });
                        }
                    })
                    .then(() => {
                        return false;
                    })
                    .catch(() => {
                        return false;
                    });
            }
        } else if (code === "-900020051") {
            // 对于特殊的返回，使用$alert提示
            let msg = data.msg;
            Vue.prototype.$alert(msg, "提示", { confirmButtonText: "确定" });
            return Promise.reject(new Error(data.msg));
        } else {
            // 其他错误
            if (d3 === "1") {
                // router.push({ path: "/errGeneral" });
                return Promise.reject(new Error(data.msg));
            } else {
                let msg = data.msg;
                if (msg.indexOf("黑名单") !== -1) {
                    msg = "您的信息正在审核，通过后将及时与您联系，请您耐心等候!";
                }
                if (
                    msg.indexOf("银行卡") !== -1 &&
                    (msg.indexOf("未") !== -1 || msg.indexOf("没有") !== -1) &&
                    (msg.indexOf("开通") !== -1 || msg.indexOf("办理") !== -1) &&
                    msg.indexOf("无卡业务") !== -1
                ) {
                    let msg =
                        "尊敬的客户，您的银行卡尚未开通无卡业务，您可以按照如下操作开通：下载云闪付APP客户端——注册用户名登陆（若已有注册用户名请直接登陆）——点击“我的卡包”-“添卡”（按照页面提示进行相应操作，勿跳过任何步骤）——点击“我的卡包”--“卡列表”点击已绑定银行卡，然后查看卡详情，若页面出现了实线框内在线支付，表示已开通；若是在线支付字样是灰色，则表示未开通，点击页面的开通在线支付去操作。";
                    let html =
                        '<div style="text-align: justify" class="user-info-check"><i class="fa fa-exclamation-triangle" aria-hidden="true"></i>' +
                        msg +
                        "</div>";
                    Vue.prototype.$alert(html, "支付失败", {
                        dangerouslyUseHTMLString: true,
                        confirmButtonText: "我已知晓",
                        customClass: "info-check"
                    });
                    return Promise.reject(new Error(data.msg));
                }
                Vue.prototype.$message.error(msg);
                return Promise.reject(new Error(data.msg));
            }
        }
    },
    function (error) {
        // 响应错误
        log.fatal("请求响应错误: ", error);
        if (error.toString() === "Cancel") {
            return Promise.reject(error);
        }
        // reqLoader.close()
        // let errMsg = error.message;
        // if (
        //     errMsg &&
        //     (errMsg.indexOf("network") !== -1 || errMsg.indexOf("timeout") !== -1)
        // ) {
        //     // 跳转网络错误页面
        //     // router.push({ path: "/errNetwork" });
        // } else {
        //     // 跳转统一致命错误提示页面
        //     // router.push({ path: "/errGeneral" });
        // }
        // 关闭loading效果
        const loader = error.config && reqLoader[error.config._loader];
        if (loader) {
            loader.close();
        }
        // todo 调用日志接口通知服务端本次请求在前端发生错误
        return Promise.reject(error);
    }
);

export default function (param) {
    count++;
    if (param.loader || param.loader === undefined) {
        let loaderConf = {
            lock: true,
            text:
                param.url.indexOf("/acct/ocr/upload") > -1
                    ? "loading（上传图片较大，请耐心等待）"
                    : "loading",
            background: "rgba(255, 255, 255, 0.5)",
            fullscreen: false
        };
        Object.assign(loaderConf, param.loader || {});
        if (loaderConf.target && loaderConf.background === "rgba(255, 255, 255, 0.5)") {
            loaderConf.background = "rgba(255, 255, 255, 0.8)";
        }
        reqLoader[count] = openRequestLoading(loaderConf);
        param._loader = count;
    }
    if (conf.session_mode === "1") {
        param.headers = Object.assign({}, param.headers || {}, {
            token: cache.getSessionToken(),
            g_systemtype: conf.systemtype,
            systemtype: conf.systemtype
        });
    }
    log.info("发送请求 [" + param.url + "]: ", param);
    return axios.request(param);
}
